import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/cn';

/**
 * Badge variant styles
 *
 * Available variants:
 * - `default` - Gray background (default)
 * - `primary` - Blue background
 * - `success` - Green background
 * - `warning` - Yellow/amber background
 * - `error` - Red background
 * - `danger` - Red background (alias for error)
 * - `info` - Blue background (alias for primary)
 * - `secondary` - Gray background (alias for default)
 * - `accent` - Amber/gold background
 */
const badgeVariants = cva(
  'inline-flex items-center rounded-full font-medium',
  {
    variants: {
      variant: {
        default: 'bg-mv-gray-100 text-mv-gray-700',
        primary: 'bg-mv-blue-100 text-mv-blue-900',
        success: 'bg-green-50 text-mv-green-700',
        warning: 'bg-mv-warning-50 text-mv-warning-700',
        error: 'bg-mv-error-50 text-mv-error-700',
        accent: 'bg-mv-amber-100 text-mv-amber-800',
        outline: 'border border-mv-gray-300 bg-white text-mv-gray-700',
        // Aliases for component compatibility
        info: 'bg-mv-blue-100 text-mv-blue-900',
        secondary: 'bg-mv-gray-100 text-mv-gray-700',
        danger: 'bg-mv-error-50 text-mv-error-700',
        destructive: 'bg-mv-error-50 text-mv-error-700',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, size, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant, size }), className)} {...props} />;
}
